import { useState, type KeyboardEvent } from 'react';
import {
  CASHBOX_FALLBACK_RATE_TO_GEL,
  CASHBOX_FORM_OP_TYPES_IN,
  CASHBOX_FORM_OP_TYPES_OUT,
  deriveDirection,
} from '@mnp/shared';
import type {
  CurrencyCode,
  OperationDirection,
  OperationType,
  PaymentMethod,
} from '../../../lib/cashboxApi.js';

// A single payment method inside a deal: a client/contractor settlement can be
// split across cash + transfer + debt, each with its own amount and currency.
export type MethodDraft = {
  type: OperationType; // form offers cash | transfer | debt (europe is legacy)
  amountGel: string;
  currencyCode: CurrencyCode | 'GEL';
  currencyAmount: string;
};

export type DealDraft = {
  id: string; // stable React key — prevents per-deal local state bleeding across add/remove
  methods: MethodDraft[]; // ≥1 — each becomes its own cashbox operation
  tracks: string; // legacy linear text — kept for backwards-compat parsing
  trackList: string[]; // chip-list — canonical source post-redesign
  trackDraft: string;
  paymentMethod: PaymentMethod | '';
  account: string;
  payerName: string;
  comment: string;
  opType: string;
  collapsed: boolean;
  showAdvanced: boolean;
};

type Props = {
  deal: DealDraft;
  index: number;
  canRemove: boolean;
  canEditMethods?: boolean;
  /** Факт БД при редактировании: пилюля легаси-строки (opType вне словаря)
   * показывает сохранённое направление, а не UI-фолбэк (фикс ревью). */
  storedDirection?: OperationDirection;
  onChange: (patch: Partial<DealDraft>) => void;
  onRemove: () => void;
};

const CURRENCY_OPTIONS: Array<CurrencyCode | 'GEL'> = ['GEL', 'EUR', 'USD', 'RUB', 'UAH', 'USDT'];

// Flows offered in the operation form. "europe" is intentionally absent — it is
// the scan→payment (QR) flow, not a manual till method. "debt" = amount owed
// that did not move through the till (excluded from the cash balance).
const FORM_FLOWS: OperationType[] = ['cash', 'transfer', 'debt'];

// Ориентировочные курсы к GEL — единый источник в @mnp/shared
// (CASHBOX_FALLBACK_RATE_TO_GEL + contract-тест), вынесены из локального хардкода
// (волна 6, rub-gel-rate-constant). Это статичный ОРИЕНТИР для подсказки, не
// боевой курс; живое подключение getRates() — отдельная карточка.
const RATE_TO_GEL: Record<CurrencyCode | 'GEL', number> = CASHBOX_FALLBACK_RATE_TO_GEL;

// Карточка cashbox-optype-direction: списки формы строятся из канонического
// словаря @mnp/shared (служебные типы вроде "pickup" в форму не попадают).
// Локальные массивы OP_TYPES_IN/OUT удалены — правило живёт в одном месте.
const OP_TYPES = [...CASHBOX_FORM_OP_TYPES_IN, ...CASHBOX_FORM_OP_TYPES_OUT];

export function directionFromOpType(
  opType: string,
  storedDirection?: OperationDirection,
): OperationDirection {
  // Канон — deriveDirection (@mnp/shared): в т.ч. служебный "pickup" теперь
  // корректно показывается приходом. Для строк вне словаря (легаси-операции
  // из старых CSV) показываем СОХРАНЁННОЕ направление из БД, если оно есть
  // (фикс ревью: пилюля показывала 'expense' для income-строк — ложь, которая
  // ещё и уходила в payload). Фолбэк expense — только для новых строк формы.
  return deriveDirection(opType) ?? storedDirection ?? 'expense';
}

export function createEmptyMethod(type: OperationType = 'cash'): MethodDraft {
  return { type, amountGel: '', currencyCode: 'GEL', currencyAmount: '' };
}

let dealSeq = 0;

export function createEmptyDeal(): DealDraft {
  return {
    id: `deal-${++dealSeq}`,
    methods: [createEmptyMethod()],
    tracks: '',
    trackList: [],
    trackDraft: '',
    paymentMethod: '',
    account: '',
    payerName: '',
    comment: '',
    opType: 'Выручка',
    collapsed: false,
    showAdvanced: false,
  };
}

function methodGel(method: MethodDraft): number {
  const native = parseFloat(method.amountGel);
  return Number.isFinite(native) && native > 0 ? native : 0;
}

function dealGel(deal: DealDraft): number {
  return deal.methods.reduce((sum, m) => sum + methodGel(m), 0);
}

function methodLabel(m: PaymentMethod | ''): string {
  if (m === 'transfer') return 'Перевод';
  if (m === 'terminal') return 'Терминал';
  if (m === 'card') return 'Карта';
  return '';
}

export function flowLabel(t: OperationType): string {
  if (t === 'cash') return 'Наличные';
  if (t === 'transfer') return 'Безнал';
  if (t === 'debt') return 'Долг';
  return 'Европа';
}

function summaryText(deal: DealDraft): string {
  const parts = deal.methods
    .filter((m) => methodGel(m) > 0)
    .map((m) => `${flowLabel(m.type)} ${methodGel(m)}`);
  if (parts.length === 0) return '';
  return `${parts.join(' + ')} GEL · ${deal.opType}`;
}

export function DealBlock({
  deal,
  index,
  canRemove,
  canEditMethods = true,
  storedDirection,
  onChange,
  onRemove,
}: Props) {
  const prefix = `deal-${index}`;
  const collapsed = deal.collapsed;
  const direction = directionFromOpType(deal.opType, storedDirection);
  const [trackDraft, setTrackDraft] = useState('');

  const toggleCollapse = () => onChange({ collapsed: !collapsed });
  const toggleAdvanced = () => onChange({ showAdvanced: !deal.showAdvanced });

  const updateMethod = (mi: number, patch: Partial<MethodDraft>) => {
    onChange({ methods: deal.methods.map((m, i) => (i === mi ? { ...m, ...patch } : m)) });
  };
  const addMethod = () => {
    onChange({ methods: [...deal.methods, createEmptyMethod('debt')] });
  };
  const removeMethod = (mi: number) => {
    if (deal.methods.length <= 1) return;
    onChange({ methods: deal.methods.filter((_, i) => i !== mi) });
  };

  const addTrack = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    if (deal.trackList.includes(t)) {
      setTrackDraft('');
      return;
    }
    const next = [...deal.trackList, t];
    onChange({ trackList: next, trackDraft: '', tracks: next.join('\n') });
    setTrackDraft('');
  };

  const removeTrack = (i: number) => {
    const next = deal.trackList.filter((_, idx) => idx !== i);
    onChange({ trackList: next, tracks: next.join('\n') });
  };

  const onTrackKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTrack(trackDraft);
    } else if (e.key === 'Backspace' && trackDraft === '' && deal.trackList.length > 0) {
      e.preventDefault();
      removeTrack(deal.trackList.length - 1);
    }
  };

  const summary = summaryText(deal);

  return (
    <section
      className="cb-deal"
      data-testid="deal-block"
      aria-label={`Сделка ${index + 1}`}
      style={{
        border: '1px solid var(--mnp-border-soft)',
        borderRadius: 8,
        marginBottom: 10,
        background: '#fff',
      }}
    >
      <header
        className="cb-deal-head"
        onClick={toggleCollapse}
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          padding: '10px 12px',
          cursor: 'pointer',
          borderBottom: collapsed ? 'none' : '1px solid var(--mnp-border-soft)',
        }}
        data-testid={`${prefix}-head`}
      >
        <span
          className="cb-deal-index"
          style={{ fontWeight: 700, color: 'var(--mnp-fg-2)', fontSize: 13 }}
        >
          #{index + 1}
        </span>
        <span
          className="cb-deal-summary"
          style={{ flex: 1, fontSize: 13, color: summary ? 'var(--mnp-fg-1)' : 'var(--mnp-fg-3)' }}
        >
          {summary || 'Введите сумму и выберите метод…'}
        </span>
        <span
          className="cb-deal-action"
          aria-hidden
          style={{
            display: 'inline-block',
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
            transition: 'transform 120ms',
            color: 'var(--mnp-fg-3)',
            fontSize: 12,
          }}
        >
          ▼
        </span>
        {canRemove && (
          <button
            className="btn-admin ghost"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label="Удалить сделку"
            style={{ padding: '2px 8px', fontSize: 12 }}
          >
            ×
          </button>
        )}
      </header>

      {!collapsed && (
        <div className="cb-deal-body" style={{ padding: 12 }}>
          {/* Методы оплаты — несколько строк (тип + сумма + валюта) */}
          <div className="cb-field">
            <label>Метод оплаты</label>
            <div className="cb-methods" data-testid={`${prefix}-methods`}>
              {deal.methods.map((method, mi) => (
                <MethodRow
                  key={mi}
                  prefix={prefix}
                  methodIndex={mi}
                  amountTestId={index === 0 && mi === 0 ? 'amount-input' : undefined}
                  method={method}
                  canRemove={canEditMethods && deal.methods.length > 1}
                  onChange={(patch) => updateMethod(mi, patch)}
                  onRemove={() => removeMethod(mi)}
                />
              ))}
            </div>
            {canEditMethods && (
              <button
                type="button"
                className="btn-admin ghost"
                onClick={addMethod}
                data-testid={`${prefix}-add-method`}
                style={{ marginTop: 6, fontSize: 12, padding: '4px 10px' }}
              >
                + Добавить метод оплаты
              </button>
            )}
          </div>

          {/* Тип операции + dir-pill */}
          <div className="cb-field">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Тип операции
              <span
                className={`cb-dir-pill cb-dir-pill-${direction}`}
                data-testid={`${prefix}-dir-pill`}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background:
                    direction === 'income'
                      ? 'var(--mnp-green-100, #dcfce7)'
                      : 'var(--mnp-amber-100, #fef3c7)',
                  color:
                    direction === 'income'
                      ? 'var(--mnp-green-700, #15803d)'
                      : 'var(--mnp-amber-700, #b45309)',
                }}
              >
                {direction === 'income' ? '⬇ приход' : '⬆ расход'}
              </span>
            </label>
            <select
              className="input-admin"
              value={deal.opType}
              onChange={(e) => onChange({ opType: e.target.value })}
              data-testid={`${prefix}-op-type-select`}
            >
              <optgroup label="Приход">
                {CASHBOX_FORM_OP_TYPES_IN.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Расход">
                {CASHBOX_FORM_OP_TYPES_OUT.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </optgroup>
              {!(OP_TYPES as readonly string[]).includes(deal.opType) && (
                <option value={deal.opType}>{deal.opType}</option>
              )}
            </select>
          </div>

          {/* Tracks chips */}
          <div className="cb-field">
            <label>Трек-номера <span style={{ color: 'var(--mnp-fg-3)', fontWeight: 400 }}>(необязательно)</span></label>
            <div
              className="cb-tracks"
              data-testid={`${prefix}-tracks`}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                padding: '6px 8px',
                border: '1px solid var(--mnp-border-soft)',
                borderRadius: 6,
                background: '#fff',
                alignItems: 'center',
                minHeight: 38,
              }}
            >
              {deal.trackList.map((t, i) => (
                <span
                  key={i}
                  className="cb-track-chip"
                  style={{
                    background: 'var(--mnp-teal-100, #ccfbf1)',
                    color: 'var(--mnp-teal-800, #115e59)',
                    padding: '3px 8px',
                    borderRadius: 999,
                    fontSize: 12,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTrack(i)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 14,
                      color: 'inherit',
                      lineHeight: 1,
                    }}
                    aria-label={`Удалить трек ${t}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                value={trackDraft}
                onChange={(e) => setTrackDraft(e.target.value)}
                onKeyDown={onTrackKeyDown}
                onBlur={() => {
                  if (trackDraft.trim()) addTrack(trackDraft);
                }}
                placeholder={
                  deal.trackList.length ? 'Ещё трек…' : 'Трек и Enter (можно без трека)'
                }
                data-testid={index === 0 ? 'tracks-input' : `${prefix}-tracks-input`}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  flex: 1,
                  minWidth: 140,
                  fontSize: 13,
                  padding: '4px 0',
                }}
              />
            </div>
          </div>

          {/* Toggle Дополнительно */}
          <button
            type="button"
            className="cb-collapse"
            onClick={toggleAdvanced}
            data-testid={`${prefix}-toggle-advanced`}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--mnp-fg-2)',
              fontSize: 12,
              padding: '8px 0',
            }}
          >
            {deal.showAdvanced ? '▲ Скрыть дополнительно' : '▼ Дополнительно'}
          </button>

          {deal.showAdvanced && (
            <div className="cb-advanced">
              <div className="cb-field">
                <label>Способ оплаты для AMO</label>
                <select
                  className="input-admin"
                  value={deal.paymentMethod}
                  onChange={(e) =>
                    onChange({ paymentMethod: e.target.value as PaymentMethod | '' })
                  }
                  data-testid={`${prefix}-payment-method-select`}
                >
                  <option value="">- не выбрано -</option>
                  <option value="transfer">Перевод</option>
                  <option value="terminal">Терминал</option>
                  <option value="card">Карта</option>
                </select>
              </div>

              <div className="cb-field">
                <label>Счёт</label>
                <input
                  className="input-admin"
                  value={deal.account}
                  onChange={(e) => onChange({ account: e.target.value })}
                  placeholder="Выручка"
                />
              </div>

              <div className="cb-field">
                <label>Плательщик</label>
                <input
                  className="input-admin"
                  value={deal.payerName}
                  onChange={(e) => onChange({ payerName: e.target.value })}
                  placeholder="ФИО или название контрагента"
                />
              </div>

              <div className="cb-field">
                <label>Комментарий</label>
                <textarea
                  className="input-admin"
                  rows={2}
                  value={deal.comment}
                  onChange={(e) => onChange({ comment: e.target.value })}
                  placeholder="Любые детали"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function MethodRow({
  prefix,
  methodIndex,
  amountTestId,
  method,
  canRemove,
  onChange,
  onRemove,
}: {
  prefix: string;
  methodIndex: number;
  amountTestId?: string;
  method: MethodDraft;
  canRemove: boolean;
  onChange: (patch: Partial<MethodDraft>) => void;
  onRemove: () => void;
}) {
  const mp = `${prefix}-method-${methodIndex}`;
  const native = parseFloat(method.amountGel);
  const cur = parseFloat(method.currencyAmount);
  const showConvHint =
    method.currencyCode !== 'GEL' &&
    Number.isFinite(native) &&
    native > 0 &&
    Number.isFinite(cur) &&
    cur > 0;

  return (
    <div
      className="cb-method-row"
      data-testid={mp}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr minmax(90px, 110px) 28px', gap: 6, alignItems: 'end' }}>
        <div className="cb-field" style={{ margin: 0 }}>
          <select
            className="input-admin"
            value={method.type}
            onChange={(e) => onChange({ type: e.target.value as OperationType })}
            data-testid={`${mp}-type`}
            aria-label="Метод оплаты"
          >
            {FORM_FLOWS.map((t) => (
              <option key={t} value={t}>
                {flowLabel(t)}
              </option>
            ))}
          </select>
        </div>
        <div className="cb-field" style={{ margin: 0 }}>
          <input
            className="input-admin"
            type="number"
            min="0"
            step="0.01"
            placeholder="Сумма GEL"
            value={method.amountGel}
            onChange={(e) => onChange({ amountGel: e.target.value })}
            data-testid={amountTestId ?? `${mp}-amount`}
          />
        </div>
        <div className="cb-field" style={{ margin: 0 }}>
          <select
            className="input-admin"
            value={method.currencyCode}
            onChange={(e) => onChange({ currencyCode: e.target.value as CurrencyCode | 'GEL' })}
            data-testid={`${mp}-currency`}
            aria-label="Валюта"
          >
            {CURRENCY_OPTIONS.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="btn-admin ghost"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label="Удалить метод"
          data-testid={`${mp}-remove`}
          style={{ padding: '6px 8px', fontSize: 13, visibility: canRemove ? 'visible' : 'hidden' }}
        >
          ×
        </button>
      </div>

      {method.currencyCode !== 'GEL' && (
        <div className="cb-field" style={{ margin: 0 }}>
          <input
            className="input-admin"
            type="number"
            min="0"
            step="0.01"
            placeholder={`Сумма в ${method.currencyCode}`}
            value={method.currencyAmount}
            onChange={(e) => onChange({ currencyAmount: e.target.value })}
            data-testid={`${mp}-currency-amount`}
          />
          {showConvHint && (
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--mnp-fg-3)' }}>
              Ориентир (≈): 1 {method.currencyCode} ≈ {RATE_TO_GEL[method.currencyCode]} GEL →{' '}
              <b>≈ {Math.round(cur * RATE_TO_GEL[method.currencyCode] * 100) / 100} GEL</b>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export { dealGel };
